<template>
  <v-card elevation="2" v-resize="onResize" class="pa-4">
    <div class="d-flex flex-wrap">
      <div>
        <SelectionStat
          :filtered-length="filteredPoems.length"
          :total-length="poemsTotal"
          :stat="stat"
        />
        <ResetButton
          :text="$t('app.resetFilters')"
          :show="isFiltered"
          @reset="resetAll"
        />
      </div>
      <v-spacer></v-spacer>
      <div class="mx-n2">
        <VisibilityToggleButton
          v-model="showTimeline"
          :show-text="$t('timeline.showTimeline')"
          :hide-text="$t('timeline.hideTimeline')"
        />
        <InstructionIcon v-model="showInstruction" />
        <InstructionList
          :show="showInstruction"
          :list="$t('timeline.instructions')"
        />
      </div>
    </div>
    <div
      :class="{ 'd-flex': showTimeline }"
      class="flex-wrap align-center"
      v-show="showTimeline"
    >
<!--       <PlayControls
        class="mt-4"
        :value="brushedYearRange"
        :max-value="yearRange[1]"
        :none-empty-values="yearsWithData"
        @input="brushEnded"
      /> -->
      <ModeToggleButtons
        class="mt-4"
        v-model="selectionMode"
        :modes="selectionModes"
        :label="$t('timeline.selectionMode')"
        id="timeline-selection-mode"
      />
      <ModeToggleButtons
        class="mt-4"
        v-model="barArrangementMode"
        :modes="barArrangementModes"
        :label="$t('timeline.barArrangementMode')"
        id="timeline-bar-arrangement-mode"
      />
      <ModeToggleButtons
        class="mt-4"
        v-model="yValueMode"
        :modes="yValueModes"
        :label="$t('timeline.yValueMode')"
        id="timeline-y-value-mode"
      >
        <div class="ml-n2">
          <HelpIcon>{{ $t("timeline.yValueModeHelpText") }}</HelpIcon>
        </div>
      </ModeToggleButtons>
    </div>
    <div :class="{ 'd-flex': showTimeline }" v-show="showTimeline">
      <v-spacer></v-spacer>
      <SentimentsToggle
        :sentiments="allSentiments"
        :get-opacity="getLegendOpacity"
        :color="color"
        @toggle="toggleSentiment"
      />
    </div>
    <div v-show="showTimeline">
      <!-- Focus -->
      <FocusChart
        v-if="dataByYear.length > 0"
        :width="width"
        :period-bar-height="periodBarHeight"
        :brushed-year-range="brushedYearRange"
        :data-by-year="dataByYear"
        :periods="periods"
        :all-years="allYears"
        :color="color"
        :get-opacity="getBlockOpacity"
        :get-year-opacity="getYearOpacity"
        :format-year-tick="formatYearTick"
        :y-value-mode="yValueMode"
        :format-y-tick="formatYTick"
        :sentiments="allSentiments"
        :bar-arrangement-mode="barArrangementMode"
        @toggle-year="toggleTick"
        @toggle-block="toggleBlock"
        @enter-block="blockEntered"
        @leave-block="blockLeft"
      />
      <!-- Context -->
      <ContextChart
        v-if="dataByYear.length > 0"
        :width="width"
        :period-bar-height="periodBarHeight"
        :year-range="yearRange"
        :brushed-year-range="brushedYearRange"
        :data-by-year="dataByYear"
        :periods="periods"
        :all-years="allYears"
        :color="color"
        :get-opacity="getBlockOpacity"
        :get-year-opacity="getYearOpacity"
        :format-year-tick="formatYearTick"
        :y-value-mode="yValueMode"
        :sentiments="allSentiments"
        :bar-arrangement-mode="barArrangementMode"
        :format-y-tick="formatYTick"
        @brush-ended="brushEnded"
      />
    </div>
    <Tooltip
      :tooltip-data="tooltipData"
      :show="showTooltip"
      :position="pointerPosition"
    />
  </v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { groups, sum } from "d3-array";
import { scaleOrdinal } from "d3-scale";
import { format } from "d3-format";
import "d3-transition";
import InstructionList from "@/components/InstructionList.vue";
import InstructionIcon from "@/components/InstructionIcon.vue";
import VisibilityToggleButton from "@/components/VisibilityToggleButton.vue";
import ResetButton from "@/components/ResetButton.vue";
import SentimentsToggle from "./SentimentsToggle.vue";
import FocusChart from "./FocusChart.vue";
import ContextChart from "./ContextChart.vue";
import SelectionStat from "./SelectionStat.vue";
/*import PlayControls from "./PlayControls.vue";*/
import ModeToggleButtons from "@/components/ModeToggleButtons";
import HelpIcon from "@/components/HelpIcon.vue";
import Tooltip from "./Tooltip.vue";

export default {
  components: {
    InstructionList,
    InstructionIcon,
    VisibilityToggleButton,
    ResetButton,
    SentimentsToggle,
    FocusChart,
    ContextChart,
    SelectionStat,
    /*PlayControls,*/
    ModeToggleButtons,
    HelpIcon,
    Tooltip
  },
  data: () => ({
    width: 1200,
    brushedYearRange: [], // brush
    highlightedSentiments: [], // legend
    highlightedYears: [], // ticks
    highlightedYearsSentiments: [], // block
    periods: [
      { start: 1895, end: 1898, location: "Tuebingen" },
      { start: 1899, end: 1903, location: "Basel" },
      { start: 1904, end: 1911, location: "Gaienhofen" },
      { start: 1912, end: 1918, location: "Bern" },
      { start: 1919, end: 1962, location: "Montagnola" }
    ],
    periodBarHeight: 18,
    showInstruction: false,
    showTimeline: true,
    selectionMode: "multiple",
    selectionType: null, // ["brush", "legend", "tick", "block"]
    barArrangementMode: "stacked",
    yValueMode: "count", // ["count", "percentageYear", "percentageTotal"],
    showTooltip: false,
    pointerPosition: [0, 0],
    tooltipData: null
  }),
  computed: {
    ...mapState([
      "allYears",
      "allSentiments",
      "allYearsSentiments",
      "filteredPoems",
      "isFiltered",
      "poemsGroupYearSentiment"
    ]),
    ...mapGetters(["poemsTotal"]),
    dataByYear() {
      return Object.freeze(
        groups(
          this.poemsGroupYearSentiment.map(d => ({
            year: d.key[0],
            sentiment: d.key[1],
            value: d.value
          })),
          d => d.year
        ).map(([year, values]) => {
          const poemsBySentiment = values.sort(
            (a, b) =>
              this.allSentiments.indexOf(b.sentiment) -
              this.allSentiments.indexOf(a.sentiment)
          );
          const yearPoemsTotal = sum(poemsBySentiment, d => d.value.count);
          let stacked = 0;
          poemsBySentiment.forEach(d => {
            d.value.percentageTotal = d.value.count / this.poemsTotal;
            d.value.percentageYear = d.value.count / yearPoemsTotal;
            const stackedCount = [stacked, (stacked += d.value.count)];
            const stackedPercentageTotal = stackedCount.map(
              count => count / this.poemsTotal
            );
            const stackedPercentageYear = stackedCount.map(
              count => count / yearPoemsTotal
            );
            d.stacked = {
              count: stackedCount,
              percentageTotal: stackedPercentageTotal,
              percentageYear: stackedPercentageYear
            };
          });
          const total = {
            count: sum(poemsBySentiment, d => d.value.count),
            percentageTotal: sum(
              poemsBySentiment,
              d => d.value.percentageTotal
            ),
            percentageYear: sum(poemsBySentiment, d => d.value.percentageYear)
          };
          return {
            year,
            total,
            poemsBySentiment
          };
        })
      );
    },
    yearRange() {
      const start = Math.min(this.allYears[0], this.periods[0].start);
      const end = Math.max(
        this.allYears[this.allYears.length - 1],
        this.periods[this.periods.length - 1].end
      );
      return [start, end];
    },
    color() {
      return scaleOrdinal()
        .domain(this.allSentiments)
        .range(["#66C5CC", "#FE88B1", "#9EB9F3", "#F6CF71"]);
    },
    yearsWithData() {
      return this.dataByYear.filter(d => d.total.count > 0).map(d => d.year);
    },
    hasYearRangeFilter() {
      return (
        this.brushedYearRange[0] !== this.yearRange[0] ||
        this.brushedYearRange[1] !== this.yearRange[1]
      );
    },
    hasSentimentsFilter() {
      return this.highlightedSentiments.length !== this.allSentiments.length;
    },
    hasYearsFilter() {
      return this.highlightedYears.length !== this.allYears.length;
    },
    hasYearsSentimentsFilter() {
      return (
        this.highlightedYearsSentiments.length !==
        this.allYearsSentiments.length
      );
    },
    stringifiedHighlightedYearsSentiments() {
      // To compare values, we need a stringified version because the value is an array
      return this.highlightedYearsSentiments.map(JSON.stringify);
    },
    yearsOfHighlightedYearsSentiments() {
      return this.highlightedYearsSentiments.map(d => d[0]);
    },
    stat() {
      const stat = {};
      // Year
      if (this.hasYearsFilter) {
        stat.type = "year";
        stat.years = this.highlightedYears.slice().sort();
      } else if (this.hasYearsSentimentsFilter) {
        stat.type = "yearSentiment";
        stat.years = [...this.yearsOfHighlightedYearsSentiments].sort();
      } else if (this.brushedYearRange[0] === this.brushedYearRange[1]) {
        stat.years = [this.brushedYearRange[0]];
      } else {
        stat.yearRange = [this.brushedYearRange[0], this.brushedYearRange[1]];
      }
      if (this.hasSentimentsFilter) {
        stat.type = "sentiment";
        stat.sentiments = this.allSentiments.filter(s =>
          this.highlightedSentiments.includes(s)
        );
      }
      return stat;
    },
    formatYTick() {
      switch (this.yValueMode) {
        case "count":
          return format(",");
        case "percentageYear":
        case "percentageTotal":
          return format("~%");
        default:
          return d => d;
      }
    },
    selectionModes() {
      return ["single", "multiple"].map(mode => ({
        text: this.$t(`timeline.${mode}`),
        value: mode
      }));
    },
    barArrangementModes() {
      return ["stacked", "grouped"].map(mode => ({
        text: this.$t(`timeline.${mode}`),
        value: mode
      }));
    },
    yValueModes() {
      return ["count", "percentageYear", "percentageTotal"].map(mode => ({
        text: this.$t(`timeline.${mode}`),
        value: mode
      }));
    }
  },
  mounted() {
    this.onResize();
  },
  watch: {
    allSentiments() {
      this.resetAll();
    }
  },
  methods: {
    ...mapActions(["updateHighlightedYearsSentiments"]),
    onResize() {
      this.width = this.$el.clientWidth;
    },
    formatYearTick(t, i, n, isFocus) {
      if (isFocus && this.rectWidth >= 28) return t;
      if (i === 0) return t;
      if (t >= 1900 && n[i - 1] < 1900) return t;
      return t.toString().slice(2);
    },
    getLegendOpacity(sentiment) {
      if (
        this.hasSentimentsFilter &&
        !this.highlightedSentiments.includes(sentiment)
      )
        return 0.3;
      return 1;
    },
    getBlockOpacity({ year, sentiment }) {
      if (this.hasYearsFilter && !this.highlightedYears.includes(year))
        return 0.3;
      if (
        this.hasSentimentsFilter &&
        !this.highlightedSentiments.includes(sentiment)
      )
        return 0.3;
      if (
        this.hasYearsSentimentsFilter &&
        !this.stringifiedHighlightedYearsSentiments.includes(
          JSON.stringify([year, sentiment])
        )
      )
        return 0.3;
      if (
        this.hasYearRangeFilter &&
        (year < this.brushedYearRange[0] || year > this.brushedYearRange[1])
      )
        return 0.3;
      return 1;
    },
    getYearOpacity(year) {
      if (this.hasYearsFilter && !this.highlightedYears.includes(year))
        return 0.3;
      if (
        this.hasYearsSentimentsFilter &&
        !this.yearsOfHighlightedYearsSentiments.includes(year)
      )
        return 0.3;
      if (
        this.hasYearRangeFilter &&
        (year < this.brushedYearRange[0] || year > this.brushedYearRange[1])
      )
        return 0.3;
      return 1;
    },
    /**
     * Timeline toggle filters
     *
     * 1. There are four types of interactions that trigger timeline filters:
     * A. Brush context chart year range (brush)
     * B. Toggle sentiments color legned items (legend)
     * C. Toggle focus chart year ticks (tick)
     * D. Toggle focus chart individual blocks (block)
     *
     * 2. There are two types of selection mode:
     * A. Single: select a new value will unselect the previous value
     * B. Multiple: select a new value will add to previously highlighted values
     *
     * 3. Reset when switching type
     * Brush -> Legend: no reset
     * Brush -> Tick: no reset
     * Brush -> Block: no reset
     * Legend -> Brush: no reset
     * Legend -> Tick: RESET legend
     * Legend -> Block: RESET legend
     * Tick -> Brush: no reset
     * Tick -> Legend: RESET tick
     * Tick -> Block: RESET tick
     * Block -> Brush: no reset
     * Block -> Legend: RESET block
     * Block -> Tick: RESET block
     * These can be summarized as switching from a non-brush to another non-brush type, reset.
     */
    toggleSelection({ type, value }) {
      // I. Reset according to type switching rule in 3
      if (this.selectionType !== "brush" && this.selectionType !== type) {
        if (this.hasSentimentsFilter)
          this.highlightedSentiments = this.allSentiments.slice();
        if (this.hasYearsFilter) this.highlightedYears = this.allYears.slice();
        if (this.hasYearsSentimentsFilter)
          this.highlightedYearsSentiments = this.allYearsSentiments.slice();
      }
      this.selectionType = type;

      // II. Set new selection value according to selection mode in 2
      switch (type) {
        case "brush":
          this.brushedYearRange = value;
          break;
        case "legend":
          this.highlightedSentiments = this.updateSelectionValues(
            value,
            this.highlightedSentiments,
            this.allSentiments,
            this.selectionMode
          );
          break;
        case "tick":
          this.highlightedYears = this.updateSelectionValues(
            value,
            this.highlightedYears,
            this.allYears,
            this.selectionMode
          );
          break;
        case "block":
          this.highlightedYearsSentiments = this.updateSelectionValues(
            value,
            this.highlightedYearsSentiments,
            this.allYearsSentiments,
            this.selectionMode
          );
          break;
        default:
          break;
      }

      // III. Filter year/sentiment pairs in 1
      let filtered = this.allYearsSentiments;
      if (this.hasYearRangeFilter) {
        filtered = filtered.filter(
          ([year]) =>
            year >= this.brushedYearRange[0] && year <= this.brushedYearRange[1]
        );
      }
      if (this.hasSentimentsFilter) {
        filtered = filtered.filter(([, sentiment]) =>
          this.highlightedSentiments.includes(sentiment)
        );
      }
      if (this.hasYearsFilter) {
        filtered = filtered.filter(([year]) =>
          this.highlightedYears.includes(year)
        );
      }
      if (this.hasYearsSentimentsFilter) {
        filtered = filtered.filter(d =>
          this.stringifiedHighlightedYearsSentiments.includes(JSON.stringify(d))
        );
      }

      // IV. Update the store
      this.updateHighlightedYearsSentiments(filtered);
    },
    updateSelectionValues(newValue, currentValues, allValues, mode) {
      const index =
        typeof newValue === "object"
          ? currentValues.map(JSON.stringify).indexOf(JSON.stringify(newValue))
          : currentValues.indexOf(newValue);
      if (index === -1) {
        // Current values don't include new value
        switch (mode) {
          case "single":
            return [newValue];
          case "multiple":
            return [...currentValues, newValue];
          default:
            // Should never land here
            return [];
        }
      } else {
        // Current values includes new value
        if (currentValues.length === allValues.length) {
          // Current values include all values
          return [newValue];
        } else if (
          currentValues.length === 1 &&
          (typeof newValue === "object"
            ? JSON.stringify(currentValues[0]) === JSON.stringify(newValue)
            : currentValues[0] === newValue)
        ) {
          // Current values contains only the new value
          return allValues.slice();
        } else {
          switch (mode) {
            case "single":
              return allValues.slice();
            case "multiple":
              return currentValues.filter((_, i) => i !== index);
            default:
              // Should never land here
              return [];
          }
        }
      }
    },
    brushEnded(brushedYearRange) {
      this.toggleSelection({
        type: "brush",
        value: brushedYearRange
      });
    },
    toggleSentiment(sentiment) {
      this.toggleSelection({
        type: "legend",
        value: sentiment
      });
    },
    toggleTick(year) {
      this.toggleSelection({
        type: "tick",
        value: year
      });
    },
    toggleBlock({ year, sentiment }) {
      this.toggleSelection({
        type: "block",
        value: [year, sentiment]
      });
    },
    resetAll() {
      this.brushedYearRange = this.yearRange.slice();
      this.highlightedSentiments = this.allSentiments.slice();
      this.highlightedYears = this.allYears.slice();
      this.highlightedYearsSentiments = this.allYearsSentiments.slice();
      this.updateHighlightedYearsSentiments(this.allYearsSentiments);
    },
    blockEntered(d, event) {
      this.pointerPosition = [event.clientX + 8, event.clientY + 8];
      this.tooltipData = {
        year: d.year,
        sentiment: d.sentiment,
        color: this.color(d.sentiment),
        count: d.value.count,
        percentageYear: d.value.percentageYear,
        percentageTotal: d.value.percentageTotal
      };
      this.showTooltip = true;
    },
    blockLeft() {
      this.showTooltip = false;
    }
  }
};
</script>
